import { useMemo, useReducer } from 'react';

import {
  FILTER_STATUS_RESOLVED,
  FILTER_STATUS_PENDING,
  FILTER_STATUS_LOADING,
} from '../constants';
import dataReducer, {
  REGISTER_FILTER,
  UNREGISTER_FILTER,
  SET_FILTER_TOUCHED,
  SET_FILTER_STATUS,
  SET_FILTER_INFLUENCE_STATUS,
  defaultDataState,
} from '../dataReducer';

const useDataManager = () => {
  const [state, dispatch] = useReducer(dataReducer, defaultDataState);

  const registerFilter = (path, depends) => {
    dispatch({
      type: REGISTER_FILTER,
      payload: {
        path,
        depends,
      },
    });
  };

  const unregisterFilter = (path) => {
    dispatch({
      type: UNREGISTER_FILTER,
      payload: {
        path,
      },
    });
  };

  const onFilterChange = (path) => {
    dispatch({
      type: SET_FILTER_INFLUENCE_STATUS,
      payload: {
        path,
        status: FILTER_STATUS_PENDING,
      },
    });
  };

  const onFilterLoad = (path) => {
    dispatch({
      type: SET_FILTER_STATUS,
      payload: {
        path,
        status: FILTER_STATUS_LOADING,
      },
    });
  };

  const onFilterResolve = (path) => {
    dispatch({
      type: SET_FILTER_STATUS,
      payload: {
        path,
        status: FILTER_STATUS_RESOLVED,
      },
    });
  };

  const onFilterTouch = (path) => {
    dispatch({
      type: SET_FILTER_TOUCHED,
      payload: {
        path,
      },
    });
  };

  const isFilterTouched = (path) => {
    return Boolean(state[path]?.touched);
  };

  const isFilterPending = (path) => {
    return state[path]?.status === FILTER_STATUS_PENDING;
  };

  const isStatusResolved = (status) =>
    status === FILTER_STATUS_RESOLVED || status === undefined;

  const isFilterDependsResolved = (path) => {
    return state[path]?.depends?.every((dependPath) =>
      isStatusResolved(state[dependPath]?.status)
    );
  };

  const allDependsPendingAwait = useMemo(() => {
    const getUnresolvedFilters = (list) =>
      list?.filter((path) => !isStatusResolved(state[path]?.status));

    const isFiltersPending = (list) =>
      list.length &&
      list.every((path) => state[path]?.status === FILTER_STATUS_PENDING);

    const unresolvedFilters = getUnresolvedFilters(Object.keys(state));

    if (!isFiltersPending(unresolvedFilters)) {
      return false;
    }

    return unresolvedFilters.every((path) => {
      const unresolvedFilterDepends = getUnresolvedFilters(state[path].depends);

      return isFiltersPending(unresolvedFilterDepends);
    });
  }, [state]);

  return {
    registerFilter,
    unregisterFilter,
    isFilterTouched,
    isFilterPending,
    isFilterDependsResolved,
    onFilterTouch,
    onFilterChange,
    onFilterLoad,
    onFilterResolve,
    allDependsPendingAwait,
  };
};

export default useDataManager;
